import type { PlayedCard } from '../../types/counting';

const SUIT_SYMBOLS: Record<string, { symbol: string; color: string }> = {
  S: { symbol: '\u2660', color: 'text-black' },
  H: { symbol: '\u2665', color: 'text-red-600' },
  D: { symbol: '\u2666', color: 'text-red-600' },
  C: { symbol: '\u2663', color: 'text-black' },
};

interface PlayedCardsDisplayProps {
  /** Cards currently displayed in this trick (0-4 cards) */
  trickCards: PlayedCard[];
  /** Compact mode for narrow viewports */
  compact?: boolean;
  /** Small card mode — uses compass positions but with smaller card font/padding (for N/S desktop) */
  small?: boolean;
}

// Positioning classes for each compass direction (absolute within relative container)
const POSITION_CLASSES: Record<PlayedCard['direction'], string> = {
  N: 'top-0 left-1/2 -translate-x-1/2',
  S: 'bottom-0 left-1/2 -translate-x-1/2',
  W: 'left-0 top-1/2 -translate-y-1/2',
  E: 'right-0 top-1/2 -translate-y-1/2',
};

// Compact: spread N/S further apart, W/E slightly inset to avoid overlap on small phones
const COMPACT_POSITION_CLASSES: Record<PlayedCard['direction'], string> = {
  N: 'counting-card-N left-1/2 -translate-x-1/2',
  S: 'counting-card-S left-1/2 -translate-x-1/2',
  W: 'left-0 counting-card-W',
  E: 'right-0 counting-card-E',
};

export function PlayedCardsDisplay({ trickCards, compact, small }: PlayedCardsDisplayProps) {
  const w = compact ? 'counting-played-compact-w' : 'counting-played-w';
  const h = compact ? 'counting-played-compact-h' : 'counting-played-h';
  const suitSize = compact ? 'text-2xl' : small ? 'text-xl' : 'text-4xl';
  const rankSize = compact ? 'text-xl' : small ? 'text-lg' : 'text-5xl';
  const px = compact ? 'px-3' : small ? 'px-2' : 'px-4';
  const py = compact ? 'py-1' : small ? 'py-0.5' : 'py-1';
  const gap = compact ? 'ml-1' : small ? 'ml-0.5' : 'ml-1';
  const positions = compact ? COMPACT_POSITION_CLASSES : POSITION_CLASSES;

  if (trickCards.length === 0) {
    return <div className={`${w} ${h}`} />;
  }

  return (
    <div className={`relative ${w} ${h}`}>
      {trickCards.map((pc) => {
        const suitInfo = SUIT_SYMBOLS[pc.suit];
        return (
          <span
            key={pc.direction}
            className={`absolute bg-card border border-line-strong rounded ${px} ${py} font-bold shadow-sm ${positions[pc.direction]}`}
          >
            <span className={`font-suits ${suitSize} ${suitInfo?.color ?? 'text-black'}`}>{suitInfo?.symbol ?? '?'}</span>
            <span className={`${gap} ${rankSize} font-semibold text-ink`}>{pc.rank}</span>
          </span>
        );
      })}
    </div>
  );
}
